import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {AdjustDialogRegistry} from '@/wp/utils/paper-registry';
import MaximizeOverlay from '@/pages/components/audit/adjustment/adjust/support/MaximizeOverlay';
import {WpNotesProvider} from '@/pages/components/audit/adjustment/adjust/support/WpNotesContext';
import {useAdjustmentStore, useFsAdjustStore} from '@/store/audit';
import {useAdjustDraftStore} from '@/store/audit/adjustDraftStore';
import {
    WpPeriodScopeContext,
    WpPerCheckFollowActiveTabContext,
} from '@/adjustment/hooks/WpPeriodScopeContext';
import {WpPeriodTabsUiProvider} from '@/adjustment/hooks/WpPeriodTabsUi';
import {useWpSnackbarContext} from '@/adjustment/hooks/WpSnackbarContext';
import type {ShowSnackbarFn} from '@/adjustment/adjust/dialogs/utils/working-helpers';
import {sliceToPeriodScope, resolveWpDataMode, wpDataModeNeedsYoa} from '@/utils/auditPeriodWpMulti';
import type {AuditPeriodSlice} from '@/utils/auditPeriodSplit';
import {
    clearActiveWpPeriodContext,
    setActiveWpPeriodContext,
} from '@/services/auditService/wpPeriodYoaBridge';
import {isPlTotalViewSliceKey, PL_TOTAL_VIEW_SLICE_KEY} from '@/pages/components/fs-report/plMultiTotalView';
import {WpSignoffScopeProvider} from '@/adjustment/hooks/WpSignoffScopeContext';

export type AdjustDialogLauncherProps = {
    code?: string | null;
    open: boolean;
    onClose: () => void;
    payload?: any;
    // When rendering inside SplitAdjustTabsDialog, use content mode to avoid nested dialogs
    renderMode?: 'dialog' | 'content';
    // Callback to open AdjustTabsDialog via adjustHeaderId
    onAdjustHeaderClick?: (adjustHeaderId: string) => void;
    /** Optional: parent may listen for WP snacks; save results now show in-dialog via useWpDialogSnackbar */
    onSnackbar?: ShowSnackbarFn;
};

type PendingSnack = {
    message: string;
    severity: 'warning' | 'error' | 'success';
};

function normalizePeriods(raw: any): AuditPeriodSlice[] {
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw.map((p: any, index: number) => ({
        index: p.index ?? index,
        periodFrom: String(p.periodFrom || ''),
        periodTo: String(p.periodTo || ''),
        yearOfAssessment: p.yearOfAssessment ?? null,
        key: String(p.key || `${p.periodFrom}_${p.periodTo}` || index),
        label: p.label || p.yearOfAssessment || `${p.periodFrom} – ${p.periodTo}`,
    }));
}

export default function AdjustDialogLauncher({
                                                 code,
                                                 open,
                                                 onClose,
                                                 payload,
                                                 renderMode = 'dialog',
                                                 onAdjustHeaderClick,
                                                 onSnackbar: onSnackbarProp,
                                             }: AdjustDialogLauncherProps) {
    const snackbarFromContext = useWpSnackbarContext();
    const parentNotify = onSnackbarProp ?? snackbarFromContext ?? undefined;
    const pendingSnackRef = useRef<PendingSnack | null>(null);

    /** Queue save-result snack; FsAdjustment SnackbarField renders only after WP dialog closes. */
    const enqueueSnackbar = useCallback<ShowSnackbarFn>((message, severity) => {
        pendingSnackRef.current = {message, severity};
    }, []);

    // 组件关闭时清理 fromPaper 临时数据，并在关闭后回传排队的 save snackbar
    const prevOpenRef = useRef(open);
    useEffect(() => {
        if (prevOpenRef.current && !open) {
            // dialog 从 open → close，检查是否需要清理 fromPaper
            const state = useAdjustDraftStore.getState();
            if (!state.adjustTabsDialogOpen && state.fromPaper) {
                state.clearFromPaper();
            }
            state.clearWpSessionPeriod?.();

            // TotalSingle preview draft must not linger / pollute YOA buckets
            const totalCode = String(payload?.code || code || '').trim().toUpperCase();
            if (payload?.plTotalView && totalCode) {
                const totalKey = `${totalCode}__${PL_TOTAL_VIEW_SLICE_KEY}`;
                const next = {...(state.componentDetails || {})};
                delete next[totalKey];
                useAdjustDraftStore.setState({componentDetails: next});
            }

            const pending = pendingSnackRef.current;
            pendingSnackRef.current = null;
            if (pending && parentNotify) {
                // Defer so registry WP unmount finishes before parent SnackbarField opens
                queueMicrotask(() => {
                    parentNotify(pending.message, pending.severity);
                });
            }
        }
        prevOpenRef.current = open;
    }, [open, parentNotify, payload?.plTotalView, payload?.code, code]);

    const onSnackbar = enqueueSnackbar;

    const templateRefRaw = payload?.line?.templateCodeRef || payload?.templateCodeRef || null;
    const templateRefKey = templateRefRaw ? String(templateRefRaw).trim() : null;
    const codeKey = payload?.code ? String(payload.code).toUpperCase() : null;
    const propCodeKey = code ? String(code).toUpperCase() : null;
    // WHY: registry may be templateCodeRef (e.g. CommonTransactionWp for custom nodes),
    // but WP data / WpPerCheck Per PL|BS must use the real FS code (custom_code).
    const fsCode = codeKey || propCodeKey || null;
    const key = templateRefKey || fsCode || 'NONE';
    const DialogComp = AdjustDialogRegistry[key]
        || (fsCode ? AdjustDialogRegistry[fsCode] : undefined);

    const periods = useMemo(
        () => normalizePeriods(payload?.periods),
        [payload?.periods],
    );
    const isMultiWpTabs =
        String(payload?.wpPeriodTabMode || '').toLowerCase() === 'multi'
        && periods.length >= 2;

    const [activePeriodKey, setActivePeriodKey] = useState<string>('');
    useEffect(() => {
        if (!isMultiWpTabs) {
            setActivePeriodKey('');
            return;
        }
        // On close: clear so the next open falls back to preferred slice on first paint
        if (!open) {
            setActivePeriodKey('');
            return;
        }
        // Prefer the slice the user was on (PL outer tab / opener periodScope); else first slice
        const preferred =
            String(payload?.periodScope?.sliceKey || payload?.sliceKey || '').trim()
            || periods[0]?.key
            || '';
        if (!activePeriodKey || !periods.some((p) => p.key === activePeriodKey)) {
            setActivePeriodKey(preferred);
        }
    }, [open, isMultiWpTabs, periods, activePeriodKey, payload?.periodScope?.sliceKey, payload?.sliceKey]);

    const activeSlice = useMemo(() => {
        if (!isMultiWpTabs) return null;
        return periods.find((p) => p.key === activePeriodKey) || periods[0] || null;
    }, [isMultiWpTabs, periods, activePeriodKey]);

    // Register WP session period for Drawer §5.7 (SINGLE / no periods = clear)
    useEffect(() => {
        if (!open || !DialogComp) return;
        const draft = useAdjustDraftStore.getState();
        const statement = (payload?.statement === 'PL' || payload?.statement === 'BS')
            ? payload.statement
            : ((fsCode || key).startsWith('PL-') ? 'PL' : 'BS');
        const spanType = periods.length >= 2 ? 'MULTI' : (payload?.spanType === 'MULTI' ? 'MULTI' : 'SINGLE');
        if (spanType === 'MULTI' && periods.length > 0) {
            const sliceKey = activeSlice?.key
                || payload?.periodScope?.sliceKey
                || payload?.sliceKey
                || null;
            draft.setWpSessionPeriod?.({
                spanType: 'MULTI',
                statement,
                code: fsCode || key,
                sliceKey,
                periods,
            });
        } else {
            draft.clearWpSessionPeriod?.();
        }
    }, [
        open,
        DialogComp,
        key,
        fsCode,
        payload?.statement,
        payload?.spanType,
        payload?.periodScope?.sliceKey,
        payload?.sliceKey,
        periods,
        activeSlice?.key,
    ]);

    // Sanitize early so YOA bridge effect can run before any early return (Rules of Hooks).
    const {companyName, ...rest} = (payload || {}) as any;

    const periodScopeFromSlice = activeSlice
        ? sliceToPeriodScope(activeSlice, {
            isFirstSlice: periods[0]?.key === activeSlice.key,
        })
        : null;
    const effectivePeriodScope = isMultiWpTabs && periodScopeFromSlice
        ? periodScopeFromSlice
        : (rest.periodScope || (
            rest.periodFrom && rest.periodTo
                ? {
                    periodFrom: rest.periodFrom,
                    periodTo: rest.periodTo,
                    yearOfAssessment: rest.yearOfAssessment ?? null,
                    sliceKey: rest.sliceKey || '',
                }
                : null
        ));

    // P7 §2.4: bridge YOA only for pl-slice / bs-multi (not non-whitelist BS)
    // Must stay above `if (!DialogComp) return null` — hooks cannot be conditional.
    useEffect(() => {
        if (!open || !DialogComp) {
            clearActiveWpPeriodContext();
            return;
        }
        const spanType =
            (periods.length >= 2 || payload?.spanType === 'MULTI' || rest.spanType === 'MULTI')
                ? 'MULTI'
                : 'SINGLE';
        const dataMode = resolveWpDataMode(key, spanType);
        const isPlTotalView = !!payload?.plTotalView
            || isPlTotalViewSliceKey(effectivePeriodScope?.sliceKey)
            || isPlTotalViewSliceKey(rest.sliceKey);
        const yoaCandidate =
            effectivePeriodScope?.yearOfAssessment
            ?? rest.yearOfAssessment
            ?? null;
        setActiveWpPeriodContext({
            code: fsCode || key,
            spanType,
            dataMode: isPlTotalView ? 'single' : dataMode,
            yearOfAssessment: isPlTotalView ? null : (wpDataModeNeedsYoa(dataMode) ? yoaCandidate : null),
            plTotalView: isPlTotalView,
        });
        return () => {
            clearActiveWpPeriodContext();
        };
    }, [
        open,
        DialogComp,
        key,
        fsCode,
        periods.length,
        payload?.spanType,
        payload?.plTotalView,
        rest.spanType,
        rest.sliceKey,
        effectivePeriodScope?.yearOfAssessment,
        effectivePeriodScope?.sliceKey,
        rest.yearOfAssessment,
    ]);

    if (!DialogComp) {
        return null;
    }
    // Add headerId for dynamic components to know which header to save under
    const activeHeaderId = useAdjustmentStore.getState().activeHeaderId || undefined;

    // ✅ 从 fsAdjustStore 获取 line 数据（展示层数据，包含 finalAmount、adjustment 等）
    // First try to use line data passed from parent; lookup by FS code (not registry template key)
    const fsLine = payload?.line || useFsAdjustStore.getState().getLineByCode(fsCode || key);
    const titleFromStore = fsLine?.name || fsLine?.scheduleName || null;

    const sliceKey = effectivePeriodScope?.sliceKey || rest.sliceKey || '';
    // Phase 6: MULTI WP Tabs keep a stable React key (= FS code) so Tab switches do not remount /
    // flash Skeleton. Slice isolation stays on periodScope + draftDetailKey (code__sliceKey).
    // SINGLE / non-multi: keep code__sliceKey when present (harmless) or code.
    // WHY: custom nodes share registryKey CommonTransactionWp — instance key must be fsCode.
    const dataCode = fsCode || key;
    const reactInstanceKey = isMultiWpTabs
        ? dataCode
        : (sliceKey ? `${dataCode}__${sliceKey}` : dataCode);
    const isPlTotalView = !!rest.plTotalView
        || !!payload?.plTotalView
        || isPlTotalViewSliceKey(sliceKey);

    const sanitizedPayload = {
        ...rest,
        title: titleFromStore || rest.title || 'Working Paper',
        headerId: activeHeaderId,
        annualId: rest.annualId,
        line: fsLine,
        onAdjustHeaderClick,
        onSnackbar,
        // WHY: code = FS/custom_code for perData + WP persistence; registryKey = dialog component
        code: dataCode,
        registryKey: key,
        periodFrom: effectivePeriodScope?.periodFrom ?? rest.periodFrom,
        periodTo: effectivePeriodScope?.periodTo ?? rest.periodTo,
        periodScope: effectivePeriodScope,
        sliceKey: effectivePeriodScope?.sliceKey ?? rest.sliceKey,
        yearOfAssessment: isPlTotalView
            ? null
            : (effectivePeriodScope?.yearOfAssessment ?? rest.yearOfAssessment),
        wpPeriodTabMode: rest.wpPeriodTabMode,
        periods: rest.periods,
        spanType: rest.spanType,
        /** Phase 6: hint for hooks — Tab switch must not reset initialized */
        seamlessPeriodTabs: isMultiWpTabs,
        plTotalView: isPlTotalView,
        wpPerCheckFollowActiveTab: !!rest.wpPerCheckFollowActiveTab || !!payload?.wpPerCheckFollowActiveTab,
        readOnly: isPlTotalView || !!rest.readOnly || !!payload?.readOnly,
    } as any;

    const tabsUi = isMultiWpTabs
        ? {
            periods,
            activeKey: activePeriodKey || periods[0]?.key || '',
            onChange: setActivePeriodKey,
        }
        : null;

    const notesCompanyId = Number(sanitizedPayload?.companyId) || null;
    const notesAnnualId = Number(sanitizedPayload?.annualId) || null;
    const notesFsId = Number(sanitizedPayload?.line?.fsId) || null;
    const notesInitial = (sanitizedPayload?.line as {notes?: string | null} | undefined)?.notes ?? null;

    const forceReadOnly = !!sanitizedPayload.readOnly;
    const followActiveTabPerCheck = !!sanitizedPayload.wpPerCheckFollowActiveTab;

    const dialogBody = (
        <WpSignoffScopeProvider value={{
            companyId: notesCompanyId,
            annualId: notesAnnualId,
            code: dataCode,
            yearOfAssessment: sanitizedPayload.yearOfAssessment
                ? String(sanitizedPayload.yearOfAssessment)
                : '',
            readOnly: forceReadOnly,
        }}>
        <WpPeriodScopeContext.Provider value={effectivePeriodScope}>
            <WpPerCheckFollowActiveTabContext.Provider value={followActiveTabPerCheck}>
            <WpPeriodTabsUiProvider value={tabsUi}>
                <WpNotesProvider
                    companyId={notesCompanyId}
                    annualId={notesAnnualId}
                    fsId={notesFsId}
                    initialNotes={notesInitial}
                >
                {renderMode === 'dialog' ? (
                    <DialogComp
                        key={reactInstanceKey}
                        open={open}
                        onClose={onClose}
                        onSnackbar={onSnackbar}
                        readOnly={forceReadOnly}
                        payload={sanitizedPayload}
                    />
                ) : (
                    <DialogComp
                        key={reactInstanceKey}
                        open={true}
                        onClose={() => {}}
                        onSnackbar={onSnackbar}
                        readOnly={forceReadOnly}
                        payload={sanitizedPayload}
                        embed
                    />
                )}
                </WpNotesProvider>
            </WpPeriodTabsUiProvider>
            </WpPerCheckFollowActiveTabContext.Provider>
        </WpPeriodScopeContext.Provider>
        </WpSignoffScopeProvider>
    );

    return (
        <>
            {open && (
                <React.Suspense fallback={<div>Loading...</div>}>
                    {renderMode === 'dialog' && <MaximizeOverlay open={open}/>}
                    {dialogBody}
                </React.Suspense>
            )}
        </>
    );
}
